import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  makeStyles,
} from "@material-ui/core";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const BODY_MAX = 1024;
const HEADER_MAX = 60;
const FOOTER_MAX = 60;
const DISPLAY_TEXT_MAX = 20;
const NAME_MAX = 80;

const useStyles = makeStyles(() => ({
  field: {
    marginBottom: 12,
  },
  primaryButton: {
    color: green[500],
  },
}));

const emptyForm = {
  name: "",
  body: "",
  displayText: "",
  url: "",
  header: "",
  footer: "",
};

const isHttpsUrl = value => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const buildPayload = form => {
  const trimmedBody = form.body.trim();
  const trimmedDisplayText = form.displayText.trim();
  const trimmedUrl = form.url.trim();
  const trimmedHeader = form.header.trim();
  const trimmedFooter = form.footer.trim();
  const trimmedName = (form.name || trimmedDisplayText).trim();

  if (!trimmedBody) {
    toast.error(i18n.t("messagesInput.ctaUrl.errors.bodyRequired"));
    return null;
  }
  if (!trimmedDisplayText) {
    toast.error(i18n.t("messagesInput.ctaUrl.errors.displayTextRequired"));
    return null;
  }
  if (!isHttpsUrl(trimmedUrl)) {
    toast.error(i18n.t("messagesInput.ctaUrl.errors.invalidUrl"));
    return null;
  }

  return {
    name: trimmedName || trimmedDisplayText,
    body: trimmedBody,
    displayText: trimmedDisplayText,
    url: trimmedUrl,
    ...(trimmedHeader ? { header: trimmedHeader } : {}),
    ...(trimmedFooter ? { footer: trimmedFooter } : {}),
  };
};

const CtaUrlButtonForm = ({
  open,
  onClose,
  ticketId,
  initialButton,
  onSaved,
  onSent,
}) => {
  const classes = useStyles();
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initialButton?.id);

  useEffect(() => {
    if (!open) return;
    if (initialButton) {
      setForm({
        name: initialButton.name || "",
        body: initialButton.body || "",
        displayText: initialButton.displayText || "",
        url: initialButton.url || "",
        header: initialButton.header || "",
        footer: initialButton.footer || "",
      });
      return;
    }
    setForm(emptyForm);
  }, [open, initialButton]);

  const handleClose = () => {
    if (saving) return;
    setForm(emptyForm);
    onClose();
  };

  const setField = (field, maxLen) => event => {
    const value = event.target.value;
    setForm(prev => ({
      ...prev,
      [field]: maxLen != null ? value.slice(0, maxLen) : value,
    }));
  };

  const persistButton = async payload => {
    if (isEdit) {
      const { data } = await api.put(
        `/cta-url-buttons/${initialButton.id}`,
        payload
      );
      return data;
    }
    const { data } = await api.post("/cta-url-buttons", payload);
    return data;
  };

  const sendCta = async payload => {
    await api.post(`/messages/${ticketId}/cta-url`, {
      body: payload.body,
      displayText: payload.displayText,
      url: payload.url,
      ...(payload.header ? { header: payload.header } : {}),
      ...(payload.footer ? { footer: payload.footer } : {}),
    });
  };

  const handleSave = async ({ andSend }) => {
    const payload = buildPayload(form);
    if (!payload) return;

    setSaving(true);
    try {
      const saved = await persistButton(payload);
      if (andSend) {
        await sendCta(payload);
        toast.success(i18n.t("messagesInput.ctaUrl.success"));
        if (onSaved) onSaved(saved);
        setForm(emptyForm);
        onClose();
        if (onSent) onSent();
      } else {
        toast.success(
          i18n.t(
            isEdit
              ? "messagesInput.ctaUrl.updated"
              : "messagesInput.ctaUrl.saved"
          )
        );
        if (onSaved) onSaved(saved);
        setForm(emptyForm);
        onClose();
      }
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {i18n.t(
          isEdit
            ? "messagesInput.ctaUrl.editTitle"
            : "messagesInput.ctaUrl.createTitle"
        )}
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          className={classes.field}
          label={i18n.t("messagesInput.ctaUrl.name")}
          value={form.name}
          onChange={setField("name", NAME_MAX)}
          variant="outlined"
          fullWidth
          helperText={`${form.name.length}/${NAME_MAX}`}
          disabled={saving}
        />
        <TextField
          className={classes.field}
          label={i18n.t("messagesInput.ctaUrl.header")}
          value={form.header}
          onChange={setField("header", HEADER_MAX)}
          variant="outlined"
          fullWidth
          helperText={`${form.header.length}/${HEADER_MAX}`}
          disabled={saving}
        />
        <TextField
          className={classes.field}
          label={i18n.t("messagesInput.ctaUrl.body")}
          value={form.body}
          onChange={setField("body", BODY_MAX)}
          variant="outlined"
          fullWidth
          required
          multiline
          rows={3}
          helperText={`${form.body.length}/${BODY_MAX}`}
          disabled={saving}
        />
        <TextField
          className={classes.field}
          label={i18n.t("messagesInput.ctaUrl.displayText")}
          value={form.displayText}
          onChange={setField("displayText", DISPLAY_TEXT_MAX)}
          variant="outlined"
          fullWidth
          required
          helperText={`${form.displayText.length}/${DISPLAY_TEXT_MAX}`}
          disabled={saving}
        />
        <TextField
          className={classes.field}
          label={i18n.t("messagesInput.ctaUrl.url")}
          value={form.url}
          onChange={setField("url")}
          variant="outlined"
          fullWidth
          required
          placeholder="https://"
          disabled={saving}
        />
        <TextField
          className={classes.field}
          label={i18n.t("messagesInput.ctaUrl.footer")}
          value={form.footer}
          onChange={setField("footer", FOOTER_MAX)}
          variant="outlined"
          fullWidth
          helperText={`${form.footer.length}/${FOOTER_MAX}`}
          disabled={saving}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving} color="secondary">
          {i18n.t("messagesInput.ctaUrl.cancel")}
        </Button>
        <Button
          onClick={() => handleSave({ andSend: false })}
          disabled={saving}
          color="primary"
          variant="outlined"
        >
          {saving ? (
            <CircularProgress size={22} />
          ) : (
            i18n.t("messagesInput.ctaUrl.save")
          )}
        </Button>
        {ticketId ? (
          <Button
            onClick={() => handleSave({ andSend: true })}
            className={classes.primaryButton}
            disabled={saving}
            color="primary"
            variant="contained"
          >
            {saving ? (
              <CircularProgress size={22} />
            ) : (
              i18n.t("messagesInput.ctaUrl.saveAndSend")
            )}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default CtaUrlButtonForm;
